// Favicon utility functions for dynamic favicon management

/**
 * Updates the page favicon with a new image URL
 * @param {string} imageUrl - URL of the new favicon image
 * @param {string} fallbackUrl - Fallback URL if imageUrl fails to load
 */
export const updateFavicon = (imageUrl, fallbackUrl = "/icon.svg") => {
  // Remove existing favicon links
  const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
  existingFavicons.forEach((link) => {
    link.remove();
  });

  // Create new favicon link
  const faviconLink = document.createElement("link");
  faviconLink.rel = "icon";
  faviconLink.type = "image/x-icon";
  faviconLink.href = imageUrl;

  // Add error handling for favicon loading
  faviconLink.onerror = () => {
    faviconLink.href = fallbackUrl;
  };

  document.head.appendChild(faviconLink);
};

/**
 * Updates favicon with multiple sizes for better browser support
 * @param {string} imageUrl - URL of the new favicon image
 * @param {string} fallbackUrl - Fallback URL if imageUrl fails to load
 */
export const updateFaviconWithSizes = (imageUrl, fallbackUrl = "/icon.svg") => {
  // Remove existing favicon links
  const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
  existingFavicons.forEach((link) => {
    link.remove();
  });

  // Create multiple favicon sizes for better browser support
  const faviconSizes = [
    { size: "16x16", rel: "icon" },
    { size: "32x32", rel: "icon" },
    { size: "48x48", rel: "icon" },
    { size: "96x96", rel: "icon" },
    { size: "192x192", rel: "apple-touch-icon" },
    { size: "512x512", rel: "apple-touch-icon" },
  ];

  faviconSizes.forEach(({ size, rel }) => {
    const faviconLink = document.createElement("link");
    faviconLink.rel = rel;
    faviconLink.sizes = size;
    faviconLink.type = "image/png";
    faviconLink.href = imageUrl;

    // Add error handling for favicon loading
    faviconLink.onerror = () => {
      faviconLink.href = fallbackUrl;
    };

    document.head.appendChild(faviconLink);
  });
};

/**
 * Resets favicon to default
 * @param {string} defaultUrl - Default favicon URL
 */
export const resetFavicon = (defaultUrl = "/icon.svg") => {
  // Remove existing favicon links
  const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
  existingFavicons.forEach((link) => {
    link.remove();
  });

  // Create default favicon link
  const faviconLink = document.createElement("link");
  faviconLink.rel = "icon";
  faviconLink.type = "image/svg+xml";
  faviconLink.href = defaultUrl;

  document.head.appendChild(faviconLink);
};

/**
 * Creates a canvas-based favicon from an image URL
 * Useful for converting event logos to proper favicon format
 * @param {string} imageUrl - URL of the image to convert
 * @param {number} size - Size of the favicon (default: 32)
 * @returns {Promise<string>} - Data URL of the generated favicon
 */
export const createFaviconFromImage = async (imageUrl, size = 32) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // img.crossOrigin = "anonymous";

    // Add timeout to prevent hanging
    const timeout = setTimeout(() => {
      reject(new Error("Image load timeout"));
    }, 10000); // 10 second timeout

    img.onload = () => {
      clearTimeout(timeout);
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = size;
        canvas.height = size;

        // Draw image centered and scaled to fit
        ctx.drawImage(img, 0, 0, size, size);

        const dataUrl = canvas.toDataURL("image/png");

        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = (error) => {
      clearTimeout(timeout);

      reject(new Error("Failed to load image"));
    };

    img.src = imageUrl;
  });
};

/**
 * Updates favicon with event logo, with fallback handling
 * @param {string} logoUrl - Event logo URL
 * @param {string} eventName - Event name for fallback
 * @param {string} fallbackUrl - Fallback favicon URL
 */
export const updateFaviconWithEventLogo = async (
  logoUrl,
  eventName = "Event",
  fallbackUrl = "/icon.svg"
) => {
  if (!logoUrl) {
    updateFavicon(fallbackUrl);
    return;
  }

  try {
    // Try to create a proper favicon from the logo
    const faviconDataUrl = await createFaviconFromImage(logoUrl, 32);
    updateFavicon(faviconDataUrl, fallbackUrl);
  } catch (error) {
    updateFavicon(fallbackUrl);
  }
};

/**
 * Creates a text-based favicon as fallback when no logo is available
 * @param {string} text - Text to display in favicon (usually first letter of event name)
 * @param {string} backgroundColor - Background color for favicon
 * @param {string} textColor - Text color for favicon
 * @returns {string} - Data URL of the generated favicon
 */
export const createTextFavicon = (
  text = "E",
  backgroundColor = "#3B82F6",
  textColor = "#FFFFFF"
) => {
  const size = 32;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = size;
  canvas.height = size;

  // Draw background circle
  ctx.fillStyle = backgroundColor;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  ctx.fill();

  // Draw text
  ctx.fillStyle = textColor;
  ctx.font = `${size * 0.6}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text.toUpperCase(), size / 2, size / 2);

  const dataUrl = canvas.toDataURL("image/png");

  return dataUrl;
};

/**
 * Updates favicon with event logo or creates text-based fallback
 * @param {string} logoUrl - Event logo URL
 * @param {string} eventName - Event name for text fallback
 * @param {string} fallbackUrl - Final fallback favicon URL
 */
export const updateFaviconWithEventLogoOrText = async (
  logoUrl,
  eventName = "Event",
  fallbackUrl = "/icon.svg"
) => {
  if (!logoUrl) {
    const firstLetter = eventName.charAt(0).toUpperCase() || "E";
    const textFavicon = createTextFavicon(firstLetter);
    updateFavicon(textFavicon, fallbackUrl);
    return;
  }

  try {
    // First, try to use the image URL directly as favicon

    updateFavicon(logoUrl, fallbackUrl);

    // Then try to create a proper favicon from the logo in the background
    try {
      const faviconDataUrl = await createFaviconFromImage(logoUrl, 32);
      updateFavicon(faviconDataUrl, fallbackUrl);
    } catch (processError) {
      // Keep the direct URL that was already set
    }
  } catch (error) {
    const firstLetter = eventName.charAt(0).toUpperCase() || "E";
    const textFavicon = createTextFavicon(firstLetter);
    updateFavicon(textFavicon, fallbackUrl);
  }
};
